import React, { useState, useMemo } from "react";
import {
  Stack,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import StatsBar from "@/components/stats-bar";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { SelectClientBanner } from "@/components";
import { useGetFirstSaleSuppliersQuery } from "@/services/first-sale";
import { formatCurrency } from "./utils";
import SupplierTable from "./components/supplier-table";
import ACEOpportunitiesSection from "./components/ace-opportunities-section";
import AddSupplierModal from "./components/add-supplier-modal";

const FirstSaleProgram: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const [searchQuery, setSearchQuery] = useState("");
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const isTDAdmin = user?.userRole === roles.TDADMIN;

  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const skipQuery = canSelectClients && !selectedClient && !isTDAdminTCD;

  const { data: suppliers = [] } = useGetFirstSaleSuppliersQuery(
    {
      clientId,
      ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
    },
    { skip: skipQuery }
  );

  const existingSupplierMids = useMemo(
    () => suppliers.map((s: any) => s.mid),
    [suppliers]
  );

  const stats = useMemo(() => {
    const totalSuppliers = suppliers.length;
    const totalActiveCases = suppliers.reduce((sum: number, s: any) => sum + (s.activeCases || s.casesCount || 0), 0);
    const totalSavings = suppliers.reduce((sum: number, s: any) => sum + (s.totalSavings || 0), 0);
    const needAttention = suppliers.filter((s: any) => s.status === "PENDING_REVIEW").length;
    return { totalSuppliers, totalActiveCases, totalSavings, needAttention };
  }, [suppliers]);

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <Box sx={{ p: 3 }}>
        {/* Title + subtitle */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: tokens.fontWeight.heavy,
                color: tokens.text.primary,
                mb: 0.5,
              }}
            >
              First Sale Program
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                color: tokens.text.secondary,
              }}
            >
              Manage first sale valuation across suppliers and products.
            </Typography>
          </Box>
        </Stack>

        {/* Metrics row: stats left, search + add right */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <StatsBar
            compact
            items={[
              { label: "Suppliers", value: stats.totalSuppliers },
              { label: "Active Cases", value: stats.totalActiveCases },
              { label: "Total Savings", value: formatCurrency(stats.totalSavings), color: tokens.status.success },
              { label: "Need Attention", value: stats.needAttention, color: stats.needAttention > 0 ? tokens.status.warning : tokens.text.primary },
            ]}
          />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              placeholder="Search suppliers..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 260,
                "& .MuiOutlinedInput-root": {
                  fontSize: tokens.fontSize.sm2,
                  borderRadius: tokens.radius.md,
                  bgcolor: tokens.bg.surface,
                },
              }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setAddSupplierOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary,
                borderRadius: tokens.radius.md,
                px: 2.5,
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: tokens.brand.primaryHover,
                  boxShadow: "none",
                },
              }}
            >
              Add Supplier
            </Button>
          </Stack>
        </Stack>

        {/* Suppliers Table — indexed by middleman and factory */}
        <SupplierTable suppliers={suppliers} searchQuery={searchQuery} />

        {/* ACE Opportunities */}
        <ACEOpportunitiesSection existingSupplierMids={existingSupplierMids} />
      </Box>

      <AddSupplierModal open={addSupplierOpen} onClose={() => setAddSupplierOpen(false)} />
    </Stack>
  );
};

export default FirstSaleProgram;
